import { FormStatus } from './Form';

export enum FieldType {
  Number = 'number',
  Text = 'text',
  Date = 'date',
  Select = 'select',
  TableSelect = 'selectable-table',
  Combined = 'combined',
  Calculated = 'calculated',
  File = 'file',
  Layout = 'layout',
  Divider = 'divider',
  Header = 'header',
  StringList = 'string-list',
  Report = 'report',
}

export enum SelectFieldDisplayComponent {
  Radio = 'radio',
  Dropdown = 'dropdown',
}

export enum OptionSourceType {
  CodeLibrary = 'codeLibrary',
  FieldTemplate = 'fieldTemplate',
}

export type FieldAttributesOptions = {
  value: string;
  label: string;
};

export enum LogicalOperator {
  AND = 'and',
  OR = 'or',
}

export enum ReferenceType {
  formFieldReference = 'formFieldReference',
  formReference = 'formReference',
  patientFieldReference = 'patientFieldReference',
}

export enum VisitReferenceType {
  CURRENT = 'CURRENT',
  PREVIOUS = 'PREVIOUS',
}

export enum ConditionOperation {
  EQUALS = 'EQUALS',
  NOT_EQUALS = 'NOT_EQUALS',
  GREATER_THAN = 'GREATER_THAN',
  GREATER_THAN_OR_EQUAL = 'GREATER_THAN_OR_EQUAL',
  LOWER_THAN = 'LOWER_THAN',
  LOWER_THAN_OR_EQUAL = 'LOWER_THAN_OR_EQUAL',
  CONTAINS = 'CONTAINS',
  NOT_CONTAINS = 'NOT_CONTAINS',
  IS_ANY_OF = 'IS_ANY_OF',
}

export enum ConditionValueType {
  STRING = 'string',
  NUMBER = 'number',
  DATE = 'date',
  DATETIME = 'datetime',
  TIME = 'time',
  YEAR = 'year',
  STRING_LIST = 'stringlist',
}

export enum ConditionType {
  condition = 'condition',
  conditionGroup = 'conditionGroup',
}

export type Condition = {
  type: ConditionType;
  reference: {
    type: ReferenceType;
    alias?: string;
    fieldTemplateId?: string;
    formTemplateId?: string;
    attribute?: string;
    visitReference?: VisitReferenceType;
    valueType?: ConditionValueType;
    formStatus?: FormStatus[];
    visitIndex?: string;
  };
  operation: ConditionOperation;
  value?: string | string[] | null;
};

export type ConditionGroup = {
  type: ConditionType;
  operation: LogicalOperator;
  conditions: ConditionGroup[] | Condition[];
};

export type FieldAttributes = {
  // all fields
  label: string;
  defaultValue?: string | number | string[];
  isRequired?: boolean;
  description?: string;
  isRepeatable?: boolean;

  // string field
  minLength?: number;
  maxLength?: number;
  visibleLineCount?: number;

  // number field
  minValue?: number;
  maxValue?: number;
  minNormalValue?: number;
  maxNormalValue?: number;

  // number, string
  unit?: string;

  // only for select
  options?: FieldAttributesOptions[];
  referedFieldsOptions?: FieldAttributesOptions[];
  isMultiSelect?: boolean;
  optionsSourceFieldId?: string;
  optionsSourceFieldAlias?: string;
  optionsSourceType?: OptionSourceType;
  optionsSourceCodeLibraryId?: string;
  optionsSourceFieldTemplateId?: string;
  component?: SelectFieldDisplayComponent;

  // only for date
  dateType?: string;
  defaultValueType?: string;
  cantBeEarlierThanBirthDate?: boolean;
  cantBeLaterThanVisitDate?: boolean;

  // only for calculated
  code?: string;
};

export enum HL7ImportRule {
  LOWER = 'lower',
  GREATER = 'greater',
  AVERAGE = 'average',
  FIRST = 'first',
  LAST = 'last',
}

export type FieldTemplateMappings = {
  standardCodeLibrary?: {
    fieldIdentifier?: string;
    valueMapping?: {
      [key: string]: string;
    };
  };
  externalCommunication?: {
    hl7Identifier?: string;
    hl7ImportRule?: HL7ImportRule;
    hl7OverrideEnabled: boolean;
  };
};

export type FieldTemplate = {
  id: string;
  fieldType: FieldType;
  version: number;
  alias: string;
  fieldAttributes: FieldAttributes;
  isVisible?: ConditionGroup;
  isRequired?: ConditionGroup;
  // the following 3 fields are filled in by the backend
  fieldTemplateId?: string;
  formFieldTemplateId?: string;
  formTemplateId?: string | undefined;
  formTemplateTypeId?: string;
  __v?: number | string;
  mappings?: FieldTemplateMappings;
};
